import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, ArrowRight } from "lucide-react";
import { useGetCalendarDataQuery } from "../../store/apis/attendance.api";
import {
  STAT_CARD_CONFIGS,
  getStatusCategory,
} from "../../constants/attendance";
import { CARD_CLASS } from "../../constants/dashboard";
import { ShimmerBlock } from "../common/Skeleton";
import { useHasPermission } from "../../store/hooks/useRbac";
import { PERMISSIONS } from "../../utils/rbac/permissions";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const AttendanceSnapshot: React.FC = () => {
  const canViewAttendance = useHasPermission([
    PERMISSIONS.MY_ATTENDANCE_VIEW,
    PERMISSIONS.MY_ATTENDANCE_MANAGE,
  ]);
  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();

  const { data: calendarResponse, isLoading } = useGetCalendarDataQuery({
    month,
    year,
  });

  const days = calendarResponse?.data?.days || [];

  const snapshot = useMemo(() => {
    let present = 0;
    let absent = 0;
    let leave = 0;
    const presentWithBoth: { in: string; out: string }[] = [];

    for (const d of days) {
      const cat = d.status ? getStatusCategory(d.status) : "none";
      if (cat === "present") {
        present++;
        if (d.checkInTime && d.checkOutTime) {
          presentWithBoth.push({ in: d.checkInTime, out: d.checkOutTime });
        }
      } else if (cat === "absent") {
        absent++;
      } else if (cat === "leave") {
        leave++;
      }
    }

    const avgHours =
      presentWithBoth.length > 0
        ? (
          presentWithBoth.reduce((sum, t) => {
            const h =
              (new Date(t.out).getTime() - new Date(t.in).getTime()) /
              (1000 * 60 * 60);
            return sum + Math.max(0, h);
          }, 0) / presentWithBoth.length
        ).toFixed(1)
        : "0";

    return {
      present,
      absent,
      leave,
      avgHours,
    };
  }, [days]);

  const tiles = [
    { ...STAT_CARD_CONFIGS[0], value: String(snapshot.present) },
    { ...STAT_CARD_CONFIGS[1], value: String(snapshot.absent) },
    { ...STAT_CARD_CONFIGS[2], value: String(snapshot.leave) },
    { ...STAT_CARD_CONFIGS[3], value: `${snapshot.avgHours}h` },
  ];

  return (
    <div className="bg-surface border border-border-secondary rounded-card p-4 sm:p-5 h-full flex flex-col justify-between shadow-soft">
      {/* Header */}
      <div className="flex items-start justify-between mb-4 shrink-0">
        <div className="flex items-start gap-2.5">
          <CalendarDays className="w-[18px] h-[18px] text-primary-600 mt-0.5 flex-none" />
          <div>
            <h3 className="text-heading-sm font-bold text-text-primary leading-tight">
              Attendance · {MONTH_NAMES[month - 1]}
            </h3>
            <p className="mt-0.5 text-body-sm text-text-tertiary leading-tight">
              Monthly overview & averages
            </p>
          </div>
        </div>
        {canViewAttendance && (
          <Link
            to="/attendance"
            className="text-body-sm font-bold text-primary-700 hover:text-primary-800 transition-colors flex items-center gap-1 no-underline focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none rounded"
          >
            View all
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>

      <div className="flex-1 flex flex-col justify-center">
        {isLoading ? (
          <div className="grid grid-cols-2 gap-2.5">
            {[1, 2, 3, 4].map((i) => (
              <ShimmerBlock key={i} className="h-[68px] rounded-xl" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2.5">
            {tiles.map((tile) => (
              <div
                key={tile.key}
                className="flex items-center gap-2.5 p-3 rounded-xl bg-layout-hover border border-border-secondary min-w-0"
              >
                <span
                  className="w-9 h-9 rounded-lg grid place-items-center flex-none"
                  style={{ background: tile.iconBg }}
                >
                  <tile.Icon
                    className="w-4 h-4"
                    style={{ color: tile.iconColor }}
                  />
                </span>
                <div className="min-w-0">
                  <div className="text-[18px] font-extrabold leading-none text-text-primary">
                    {tile.value}
                  </div>
                  <div className="text-caption font-semibold text-text-tertiary mt-1 truncate">
                    {tile.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceSnapshot;
